"use client";

import React from "react";
import { useQuery } from "@tanstack/react-query";
import { complianceApi } from "@/lib/api/services/compliance.api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Shield, FileText, AlertTriangle, CheckCircle, Loader2 } from "lucide-react";

interface ComplianceItem {
  label: string;
  status: "compliant" | "pending" | "non-compliant";
  lastUpdated: string;
}

interface ComplianceStats {
  policyAcknowledged: number;
  pendingReviews: number;
  trainingComplete: number;
}

export default function CompliancePage() {
  const { data: items, isLoading: itemsLoading, error: itemsError } = useQuery({
    queryKey: ["compliance-items"],
    queryFn: async () => {
      const response = await complianceApi.listAlerts({ page: 1, perPage: 10 });
      return ((response as unknown as { data?: ComplianceItem[] }).data ?? []) as ComplianceItem[];
    },
  });

  const { data: stats, isLoading: statsLoading } = useQuery<ComplianceStats>({
    queryKey: ["compliance-stats"],
    queryFn: async () => {
      const response = await complianceApi.getStats();
      return (response as unknown as { data?: ComplianceStats }).data ?? {
        policyAcknowledged: 0,
        pendingReviews: 0,
        trainingComplete: 0,
      };
    },
  });

  const isLoading = itemsLoading || statsLoading;

  if (isLoading) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-8">
        <h1 className="font-heading text-xl font-semibold tracking-tight text-foreground mb-6">Compliance</h1>
        <div className="grid gap-4 sm:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted/10 text-muted-foreground animate-pulse" />
                  <div className="space-y-2 flex-1">
                    <div className="h-3 w-1/2 bg-muted/10 rounded animate-pulse" />
                    <div className="h-5 w-1/3 bg-muted/10 rounded animate-pulse" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        <Card className="mt-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-heading font-semibold">Compliance Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-md bg-muted/10 animate-pulse">
                  <div className="h-4 w-3/4 bg-muted/10 rounded" />
                  <div className="h-5 w-1/4 bg-muted/10 rounded" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (itemsError) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-8">
        <h1 className="font-heading text-xl font-semibold tracking-tight text-foreground mb-6">Compliance</h1>
        <Card>
          <CardContent className="py-16 text-center">
            <AlertTriangle className="mx-auto h-10 w-10 text-destructive/50" />
            <h3 className="mt-4 font-heading text-base font-semibold text-foreground">Failed to load compliance data</h3>
            <p className="mt-1 text-sm text-muted-foreground">Please try again later.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const complianceItems = items ?? [
    { label: "Data Privacy Policy", status: "compliant", lastUpdated: "2026-01-15" },
    { label: "Code of Conduct", status: "compliant", lastUpdated: "2026-01-10" },
    { label: "Anti-Harassment Policy", status: "compliant", lastUpdated: "2026-01-05" },
    { label: "Safety Regulations", status: "pending", lastUpdated: "2025-12-20" },
    { label: "Equal Opportunity", status: "compliant", lastUpdated: "2026-01-12" },
  ];

  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      <h1 className="font-heading text-xl font-semibold tracking-tight text-foreground mb-6">Compliance</h1>

      <div className="grid gap-4 sm:grid-cols-3">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted/10 text-muted-foreground">
                <CheckCircle className="h-4 w-4" />
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Policy Acknowledged</p>
                <p className="text-sm font-semibold text-foreground">{stats?.policyAcknowledged ?? "All policies up to date"}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted/10 text-muted-foreground">
                <AlertTriangle className="h-4 w-4" />
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Pending Reviews</p>
                <p className="text-sm font-semibold text-foreground">{stats?.pendingReviews ?? "2 documents pending review"}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted/10 text-muted-foreground">
                <FileText className="h-4 w-4" />
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Training Complete</p>
                <p className="text-sm font-semibold text-foreground">{stats?.trainingComplete ?? "All compliance training done"}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="mt-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-heading font-semibold">Compliance Status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {complianceItems.map((item) => (
              <div key={item.label} className="flex items-center justify-between p-3 rounded-md bg-muted/10">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground">{item.label}</p>
                  <p className="text-xs text-muted-foreground">Last updated: {item.lastUpdated}</p>
                </div>
                <span className={`h-5 px-2 text-[0.6875rem] font-medium rounded-full border shrink-0 ${item.status === "compliant" ? "bg-primary/10 text-primary border-primary/20" : item.status === "pending" ? "bg-yellow-100 text-yellow-700 border-yellow-200" : "bg-muted/10 text-muted-foreground border-border"}`}>{item.status}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}